import { CurrencyPipe, formatNumber } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
    ApiTypes,
    DataLayerValue,
    FootnotesPipe,
    Platform,
    PLATFORM_TOKEN,
    RouterLinkDirective,
    TrackingFeature,
    VehicleFeature,
} from '@hyundai/ng-common-lib';
import { buildPriceWithVatFootnote } from '@hyundai/shared-lib';
import { HyundaiImage, M000C010ImageComponent } from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { switchMap } from 'rxjs';
import { BatteryOverlayComponentDialogTag } from '../../overlays/battery-overlay/battery-overlay.dialog';
import { PromiseOverlayComponentDialogTag } from '../../overlays/promise-overlay/promise-overlay.dialog';
import { VehicleFavoriteButtonComponent } from '../vehicle-favorite-button/vehicle-favorite-button.component';

@Component({
    standalone: true,
    templateUrl: './vehicle-card.component.html',
    selector: 'lib-vehicle-card',
    imports: [CurrencyPipe, RouterLinkDirective, M000C010ImageComponent, VehicleFavoriteButtonComponent, FootnotesPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class VehicleCardComponent {
    store = inject(Store);

    selectedFilter = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterSelectedPreview);

    route = inject(ActivatedRoute);
    protected readonly dialogService = inject(DialogService);
    readonly cdr = inject(ChangeDetectorRef);
    destroyRef = inject(DestroyRef);
    vehicle = input.required<ApiTypes.Vehicle>();
    platform = inject(PLATFORM_TOKEN);

    vehicleCardClicked = output();

    readonly imageError = signal(false);

    tags = computed(() => {
        return this.vehicle().tags.join(' | ');
    });

    image = computed(() => {
        const img = this.vehicle().image;
        if (!img) {
            return null;
        }

        return {
            image: img,
            desktop: { width: 390 },
            tablet: { width: 466 },
            mobile: { width: 703 },
        } satisfies HyundaiImage;
    });

    rateFootnoteSource = computed(() => {
        const { price, vatRateVisible } = this.vehicle();
        const formatted = `${formatNumber(price ?? 0, 'de-DE')} EUR`;
        return buildPriceWithVatFootnote(formatted, vatRateVisible);
    });

    link = computed(() => {
        const base = `/fahrzeugbestand/${this.vehicle().slug}`;
        if (this.platform === Platform.HCM) {
            return base;
        }

        const dealerSlug = this.route.snapshot.pathFromRoot[1].params['slug'];

        return `/${dealerSlug}${base}`;
    });

    async openPromiseOverlay(): Promise<void> {
        this.dialogService
            .openDialog(PromiseOverlayComponentDialogTag)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((result) => result.close$),
            )
            .subscribe(() => {
                this.cdr.detectChanges();
            });
    }

    async openBatteryOverlay(): Promise<void> {
        this.dialogService
            .openDialog(BatteryOverlayComponentDialogTag)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((result) => result.close$),
            )
            .subscribe(() => {
                this.cdr.detectChanges();
            });
    }

    track() {
        if (Object.keys(this.selectedFilter()).length === 0) {
            return;
        }
        const filter = this.selectedFilter() as unknown as DataLayerValue;

        this.store.dispatch(
            TrackingFeature.actions.eventTrackingPush({
                dataLayer: {
                    event: 'filter_select',
                    filter,
                },
            }),
        );
    }
}
